import { apiPost } from '../api/client'
import { pushConfigFor, type FirebaseWebConfig } from './pushConfig'

/**
 * Notificarile push din browser - `resources/js/push_notifications/firebase.js`,
 * `PopupPermissions.js`, `NotificationManager.js` + `actions/*` si
 * `public/js/notifications_banner.js` din portal, in v4.
 *
 * CE FACE, IN ORDINEA PORTALULUI
 * -----------------------------------------------------------------------------
 *  1. Cand browserul inca n-a fost intrebat (`Notification.permission ===
 *     'default'`), deseneaza banda "Notifications - You have not granted..."
 *     in `.notifications`, cu butonul "Change permissions". Inchisa o data,
 *     inchisa pana la URMATOAREA LOGARE: inchiderea se tine in localStorage
 *     impreuna cu un semn al sesiunii de login (`auth_time` din jeton, prin
 *     `/me`), care se schimba la fiecare intrare.
 *  2. Cand avem voie, cere jetonul FCM si il trimite la
 *     `POST /api/v2/notifications/token` - la FIECARE incarcare, fiindca
 *     jetonul se schimba singur si un jeton vechi e refuzat de Firebase.
 *  3. `onMessage`: un toast pe fiecare mesaj primit cu fila deschisa
 *     (remote_control / collaborate / chat_message), cu aceleasi clase de
 *     toastr ca in portal (`toast-*-commands`, `toast-*-collaborate`, in
 *     app.css) si FARA termen - stau pana le inchide omul, ca in portal.
 *
 * DE CE SDK-UL VINE DE LA GSTATIC, NU DIN PACHET
 * -----------------------------------------------------------------------------
 * Portalul il lua din npm (`firebase ^9.9.2`, ~420 KB in bundle) si tot de la
 * gstatic in lucratorul de fundal. v4 nu are `firebase` printre dependinte, si
 * o dependinta de 400 KB pentru un lucru care porneste DOAR pe gazdele din
 * `pushConfig.ts` ar fi cazut in pachetul tuturor. Se incarca la cerere, in
 * stilul compat - acelasi API (`firebase.messaging()`) pe care l-a folosit
 * portalul de la inceput.
 *
 * LUCRATORUL DE FUNDAL sta la `/app/firebase-messaging-sw.js` (din `public/`),
 * nu la radacina site-ului ca `firebase-messaging-sw.js.example`: aplicatia
 * traieste sub `/app/`, si o inregistrare cu scope `/app/` e tot ce cere
 * mesageria. Se da explicit la `getToken`, ca Firebase sa nu-l caute la
 * radacina.
 */

/** Aceeasi cheie ca in `notifications_banner.js`: o inchidere data in portal ramane data. */
export const DISMISS_KEY = 'tk_notif_banner_dismissed'

const SDK_VERSION = '9.23.0'
const SDK_BASE = `https://www.gstatic.com/firebasejs/${SDK_VERSION}/`
const SERVICE_WORKER = '/app/firebase-messaging-sw.js'

export interface PushPayload {
  notification?: { title?: string; body?: string }
  data?: { type?: string; status?: string; [key: string]: string | undefined }
}

/* ------------------------------------------------------------------------- *
 * Banda de permisiuni
 * ------------------------------------------------------------------------- */

export function bannerDismissed(mark: string, storage: Pick<Storage, 'getItem'> = window.localStorage): boolean {
  if (mark === '') return false
  try {
    return storage.getItem(DISMISS_KEY) === mark
  } catch {
    /* Stocarea poate fi oprita din browser: atunci nu se tine minte nimic si
       banda se poarta ca pana acum. */
    return false
  }
}

export function rememberDismissed(mark: string, storage: Pick<Storage, 'setItem'> = window.localStorage): void {
  if (mark === '') return
  try {
    storage.setItem(DISMISS_KEY, mark)
  } catch {
    /* idem */
  }
}

/** Markupul din `resources/templates/notifications/popup_permissions.html`. */
export function bannerHtml(): string {
  return (
    '<div class="alert alert-info alert-dismissible fade in text-center" role="alert" id="alert-notifications-permissions">' +
    '<button type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>' +
    '<h5><i class="far fa-bell"></i> <b>Notifications</b></h5>' +
    '<p>You have not granted this site any special permissions.</p>' +
    '<button type="button" class="btn btn-danger btn-sm mt-2" id="change-notifications-permissions">Change permissions</button>' +
    '</div>'
  )
}

type Permission = 'granted' | 'denied' | 'default' | 'unsupported'

/**
 * Cere voie, in AMANDOUA formele - `PopupPermissions.request`.
 *
 * `Notification.requestPermission()` intoarce o promisiune in browserele noi;
 * Safari pana la 16 (si Safari pe iPhone si azi) nu intoarce nimic si raspunde
 * printr-o functie primita ca argument. Raspunsul se ia din oricare vine primul.
 */
function requestPermission(done: (p: Permission) => void): void {
  if (typeof window.Notification === 'undefined') {
    done('unsupported')
    return
  }
  let answered = false
  const once = (p?: NotificationPermission) => {
    if (answered) return
    answered = true
    done(p || window.Notification.permission)
  }
  let asked: unknown
  try {
    asked = window.Notification.requestPermission(once)
  } catch {
    try {
      asked = window.Notification.requestPermission()
    } catch {
      once(window.Notification.permission)
      return
    }
  }
  if (asked && typeof (asked as Promise<NotificationPermission>).then === 'function') {
    ;(asked as Promise<NotificationPermission>).then(once).catch(() => once(window.Notification.permission))
  }
}

function say(box: HTMLElement, text: string, tone: 'info' | 'warning' | 'danger'): void {
  box.classList.remove('alert-info', 'alert-warning', 'alert-danger')
  box.classList.add(`alert-${tone}`)
  const p = box.querySelector('p')
  if (p) p.textContent = text
}

/**
 * Deseneaza banda si ii leaga butoanele. `onGranted` porneste jetonul in clipa
 * in care omul a dat voie, fara sa astepte urmatoarea incarcare.
 */
export function showBanner(host: Element, mark: string, onGranted: () => void): HTMLElement | null {
  host.insertAdjacentHTML('beforeend', bannerHtml())
  const box = host.querySelector<HTMLElement>('#alert-notifications-permissions')
  if (!box) return null

  const closeBanner = () => {
    /* Orice inchidere - de la X sau de la butonul de permisiuni - se tine
       minte pana la urmatoarea logare, ca in `notifications_banner.js`. */
    rememberDismissed(mark)
    box.remove()
  }
  box.querySelector('.close')?.addEventListener('click', closeBanner)

  const button = box.querySelector<HTMLButtonElement>('#change-notifications-permissions')
  button?.addEventListener('click', () => {
    button.disabled = true
    requestPermission((permission) => {
      button.disabled = false
      if (permission === 'granted') {
        /* Abia acum: intrebarea a primit raspuns, si raspunsul e da. */
        closeBanner()
        onGranted()
        return
      }
      if (permission === 'unsupported') {
        say(box, 'This browser cannot show notifications from a website.', 'warning')
        button.disabled = true
        return
      }
      if (permission === 'denied') {
        /* Odata refuzat, browserul nu mai intreaba a doua oara. */
        say(
          box,
          'Notifications are blocked for this site. Turn them back on from the padlock in the address bar, then reload the page.',
          'warning',
        )
        button.disabled = true
        return
      }
      say(box, 'The browser did not answer. Press the button again, or allow notifications from the padlock in the address bar.', 'warning')
    })
  })

  return box
}

/* ------------------------------------------------------------------------- *
 * Mesajele primite cu fila deschisa - `NotificationManager` + `actions/*`
 * ------------------------------------------------------------------------- */

/**
 * Un toast care STA pana il inchide omul (`timeOut: 0` in portal), cu
 * markupul lui toastr si clasa de iconita a portalului.
 *
 * `ui/notify.ts#toast` e cel cu termen de 10 s al lui general.js; cel de aici
 * e cel al notificarilor push, care nu au voie sa dispara singure - un
 * "Error running command on ticket #4711" pierdut cat omul era in alta fila
 * e exact ce trebuia sa nu se piarda.
 */
export function stickyToast(kind: 'success' | 'error', iconClass: string, title: string, body: string): HTMLElement {
  let box = document.getElementById('toast-container')
  if (!box) {
    box = document.createElement('div')
    box.id = 'toast-container'
    box.className = 'toast-bottom-right'
    box.setAttribute('aria-live', 'polite')
    box.setAttribute('role', 'alert')
    document.body.appendChild(box)
  }
  const el = document.createElement('div')
  el.className = `toast toast-${kind} ${iconClass}`
  el.style.display = 'block'

  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'toast-close-button'
  close.setAttribute('role', 'button')
  close.innerHTML = '&times;'
  el.appendChild(close)

  if (title) {
    const t = document.createElement('div')
    t.className = 'toast-title'
    t.textContent = title
    el.appendChild(t)
  }
  const m = document.createElement('div')
  m.className = 'toast-message'
  m.textContent = body
  el.appendChild(m)

  /* `newestOnTop: false` in portal: cel nou intra dedesubt. */
  box.appendChild(el)
  const remove = () => {
    el.remove()
    if (box.childElementCount === 0) box.remove()
  }
  el.addEventListener('click', remove)
  close.addEventListener('click', (ev) => {
    ev.stopPropagation()
    remove()
  })

  return el
}

/**
 * Ce se intampla la un mesaj - `NotificationManager.run`.
 *
 * In portal, apasarea pe toastul de comenzi deschidea bara laterala din
 * dreapta, la fila de comenzi (`userSidebar.navTo('commands')`); cel de
 * colaborare, la fila de colaborare. Bara aia nu e in v4 (`SIDEBAR_READY =
 * false` in Shell.tsx, a altei bucati), deci apasarea doar inchide toastul -
 * ca la mesajele de chat, unde asa era si in portal.
 *
 * @returns ce s-a desenat, ca proba sa se uite; null = mesaj fara `data.type`
 */
export function handlePayload(payload: PushPayload): HTMLElement | null {
  const type = payload.data?.type
  if (!type) return null
  const title = payload.notification?.title ?? ''
  const body = payload.notification?.body ?? ''

  switch (type) {
    case 'remote_control':
      return payload.data?.status === 'failed'
        ? stickyToast('error', 'toast-error-commands', title, body)
        : stickyToast('success', 'toast-success-commands', title, body)
    case 'collaborate':
    case 'chat_message':
      return stickyToast('success', 'toast-success-collaborate', title, body)
    default:
      return null
  }
}

/* ------------------------------------------------------------------------- *
 * Firebase
 * ------------------------------------------------------------------------- */

interface CompatMessaging {
  getToken(options?: { serviceWorkerRegistration?: ServiceWorkerRegistration }): Promise<string>
  onMessage(next: (payload: PushPayload) => void): void
}

interface CompatFirebase {
  initializeApp(config: FirebaseWebConfig): unknown
  apps: unknown[]
  messaging: { (): CompatMessaging; isSupported(): boolean }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve()
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`could not load ${src}`))
    document.head.appendChild(s)
  })
}

async function firebase(): Promise<CompatFirebase | null> {
  await loadScript(`${SDK_BASE}firebase-app-compat.js`)
  await loadScript(`${SDK_BASE}firebase-messaging-compat.js`)
  const fb = (window as unknown as { firebase?: CompatFirebase }).firebase
  return fb ?? null
}

/** Trimite jetonul; idempotent pe server. */
export async function sendToken(messaging: CompatMessaging, registration?: ServiceWorkerRegistration): Promise<void> {
  try {
    const token = await messaging.getToken(registration ? { serviceWorkerRegistration: registration } : undefined)
    if (!token) return
    await apiPost('/notifications/token', { token })
  } catch (err) {
    console.log('An error occurred while retrieving token. ', err)
  }
}

/**
 * `.notifications` e desenat de Shell dupa prima randare; scriptul porneste
 * inaintea ei, deci asteapta cateva cadre.
 */
function whenHost(tries = 60): Promise<Element | null> {
  return new Promise((resolve) => {
    const look = (left: number) => {
      const host = document.querySelector('.notifications')
      if (host || left <= 0) {
        resolve(host)
        return
      }
      requestAnimationFrame(() => look(left - 1))
    }
    look(tries)
  })
}

/**
 * Punctul de pornire, chemat o data din `main.tsx`.
 *
 * @param loginMark semnul sesiunii de login (`String(me.authTime)`); gol =
 *                  nu se tine minte inchiderea, banda apare ca inainte
 */
export async function startPushNotifications(loginMark: string): Promise<void> {
  const canNotify = typeof window.Notification !== 'undefined'
  const config = pushConfigFor(window.location.hostname)

  let messaging: CompatMessaging | null = null
  let registration: ServiceWorkerRegistration | undefined

  const start = async () => {
    if (!config || messaging) return
    try {
      const fb = await firebase()
      if (!fb || !fb.messaging.isSupported()) return
      if (fb.apps.length === 0) fb.initializeApp(config)
      messaging = fb.messaging()
      if ('serviceWorker' in navigator) {
        registration = await navigator.serviceWorker.register(SERVICE_WORKER)
      }
      messaging.onMessage((payload) => {
        try {
          handlePayload(payload)
        } catch (err) {
          console.log('Caught error: ', err)
        }
      })
      await sendToken(messaging, registration)
    } catch (err) {
      console.log(err)
    }
  }

  if (canNotify && window.Notification.permission === 'default' && !bannerDismissed(loginMark)) {
    const host = await whenHost()
    if (host) showBanner(host, loginMark, () => void start())
  }

  if (canNotify && window.Notification.permission === 'granted') {
    await start()
  }

  /* Si in clipa in care omul tocmai a dat voie din lacatul browserului, fara
     sa astepte urmatoarea incarcare. Numai unde exista - Safari n-are. */
  if (canNotify && 'permissions' in navigator && navigator.permissions?.query) {
    try {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          status.onchange = () => {
            if (status.state === 'granted') void start()
          }
        })
        .catch(() => {
          /* Browserul nu cunoaste permisiunea asta. Nu e o eroare. */
        })
    } catch {
      /* Idem, pentru browserele care arunca in loc sa refuze. */
    }
  }
}
